import React, {
  useState,
  useMemo,
  useRef,
  useEffect,
  useCallback,
} from 'react';
import {
  Container,
  Row,
  Col,
  InputGroup,
  InputGroupAddon,
  Input,
  Button,
  Badge,
  Collapse,
  Card,
  CardBody,
  CardFooter,
  Spinner,
  Modal,
  ModalBody,
  ModalHeader,
  ModalFooter,
} from 'reactstrap';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import {
  Switch,
  Route,
  useRouteMatch,
  Redirect,
  useHistory,
} from 'react-router-dom';
import { useStripe } from '@stripe/react-stripe-js';
import AsyncSelect from 'react-select/async';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useModalsDispatch, OPEN_PREVIEW } from '../../context/modals';

import MainLayout from '../../layouts/MainLayout';
import { PageHeader } from '../../components/PageHeader';
import CustomButton from '../../components/Button';
import { Questionare } from '../PublicForm';
import ImagePlaceholder from '../../images/templates/placeholder.jpg';
import {
  useInfinitePublicTemplateList,
  useGrabPublicTemplate,
  useBookmarkPublicTemplate,
  useUnbookmarkPublicTemplate,
  useViewPublicTemplate,
} from '../../api/hooks/public-templates';
import { getPrice, cloneElementsWithProps } from '../../utils';
import useDebounce from '../../hooks/useDebounce';
import UncontrolledTooltip from 'reactstrap/lib/UncontrolledTooltip';
import { fetcher } from '../../api/client';
import { fetchCategoryList } from '../../api/actions/categories';
import { useCategoryList } from '../../api/hooks/categories';
import { useProfile } from '../../api/hooks/profile';
import { useCreateContract } from '../../api/hooks/contracts';

import config from '../../config';
import '../../scss/home.scss';
export const reactSelectStyles = {
  menu: (provided) => ({
    ...provided,
    zIndex: 3,
    borderRadius: '0px',
  }),
  control: (provided, state) => ({
    ...provided,
    ':hover': {
      borderColor: 'none',
    },
    ':active': {
      borderColor: '#F47B33',
    },
    width: state.selectProps.width || '200px',
    borderRadius: 'none',
    height: '100%',
    boxShadow: 'none',
    border: state.isFocused ? '2px solid #F47B33!important;' : undefined,
    // backgroundColor: '#ECF0F2',
    // borderBottom: state.isDisabled ? '2px solid rgba(255, 0, 0, 0)' : '2px solid #838D91',
    // boxShadow: 'none',
    // borderColor: '#838D91',
    // borderTop: '2px solid rgba(255, 0, 0, 0)',
    // borderLeft: '2px solid rgba(255, 0, 0, 0)',
    // borderRight: '2px solid rgba(255, 0, 0, 0)',
    // borderRadius: '0',
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? '#F47B33'
      : state.isFocused
      ? '#fad1b9'
      : undefined,
    ':active': {
      ...provided[':active'],
      backgroundColor: '#F47B33',
    },
  }),
  singleValue: (provided, state) => ({
    ...provided,
    color: state.isDisabled ? '#B9C2C7' : '#0B0E10',
  }),
};

export function TemplateCatalog() {
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');

  const handleSearchChange = useCallback((value) => setSearch(value), []);
  const { t } = useTranslation('common');
  return (
    <div>
      <CatalogSearchBar
        onSearchChange={handleSearchChange}
        onCategoryChange={(value) => setCategory(value)}
      />
      <div style={{ minHeight: 3 * 300 }}>
        {search.length > 0 && (
          <PublicTemplatesFolderContainer
            label={t('templates.catalougue.folders.0')}
            type="free|premium"
            sortBy="updatedAt"
            search={search}
            category={category}
          />
        )}
        <PublicTemplatesFolderContainer
          label={t('templates.catalougue.folders.1')}
          type="free|premium"
          sortBy="mostPopular"
          // search={debouncedSearch}
          category={category}
        />
        <PublicTemplatesFolderContainer
          label={t('templates.catalougue.folders.2')}
          type="free|premium"
          sortBy="updatedAt"
          isBookmark={true}
          noResultsText={t('templates.catalougue.folders.10')}
        />
        <PublicTemplatesFolderContainer
          label={t('templates.catalougue.folders.3')}
          type="free"
          // search="{debouncedSearch}
          category={category}
        />
        <PublicTemplatesFolderContainer
          label="Premium"
          type="premium"
          // search={debouncedSearch}
          category={category}
        />
        <PublicTemplatesFolderContainer
          label={t('templates.catalougue.folders.4')}
          type="free|premium"
          // search={debouncedSearch}
          onlyExternal
          category={category}
        />
      </div>
    </div>
  );
}

export function CatalogSearchBar({ onSearchChange, onCategoryChange }) {
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState(null);
  const { data, isLoading: isLoadingCategories } = useCategoryList({
    perPage: 4,
  });
  const debouncedSearch = useDebounce(search, 500);

  const handleChange = (option) => {
    setCategory(option);
  };

  useEffect(() => {
    onSearchChange(debouncedSearch);
  }, [onSearchChange, debouncedSearch]);
  const { t } = useTranslation('common');
  return (
    <div className="bg-white">
      <Container className="p-3">
        <div className="text-center font-size-20 font-weight-bold">
          {t('templates.catalougue.title')}
        </div>
        <div className="d-flex justify-content-center p-3">
          <div style={{ width: '70%' }}>
            <InputGroup>
              <Input
                size="lg"
                onChange={(evt) => {
                  setSearch(evt.target.value);
                }}
                value={search}
              />
              <InputGroupAddon addonType="append">
                {/* <Input type="select">
                            <option>1</option>
                        </Input>  */}
                <CategorySelect
                  styles={reactSelectStyles}
                  onChange={handleChange}
                  value={category}
                />
              </InputGroupAddon>
              <InputGroupAddon addonType="append">
                <Button size="sm" color="accent-100">
                  {' '}
                  <FontAwesomeIcon icon="search" />
                  {t('templates.catalougue.search')}
                </Button>
              </InputGroupAddon>
            </InputGroup>
          </div>
        </div>
        <div className="d-flex justify-content-center p-3 align-items-center">
          <span className="text-muted">
            {t('templates.catalougue.folders.1')}
          </span>
          {data?.data?.map((cat) => (
            <Badge
              className={clsx(
                'ml-2',
                category?.value === cat._id && 'border-accent bg-accent'
              )}
              pill
              color={'light-grey'}
              onClick={() => {
                setCategory({ label: cat.name, value: cat._id });
                onCategoryChange(cat._id);
              }}
            >
              {cat.name}
            </Badge>
          ))}
          {isLoadingCategories && <Spinner className="ml-2" size="sm" />}
        </div>
      </Container>
    </div>
  );
}

function CategorySelect(props) {
  const { t } = useTranslation();

  const fetchCategoryListOptions = async (inputValue) => {
    try {
      const { data } = await fetcher(
        fetchCategoryList({ page: 1, perPage: 30, search: inputValue })
      );

      return data.map((item) => ({
        label: item.name,
        value: item._id,
        data: item,
      }));
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <AsyncSelect
      styles={reactSelectStyles}
      loadOptions={fetchCategoryListOptions}
      placeholder={t('category')}
      cacheOptions
      defaultOptions
      isClearable
      {...props}
    />
  );
}

export function PublicTemplatesFolderContainer({
  label,
  jurisdiction,
  category,
  type = 'free',
  defaultIsOpen,
  noResultsText,
  populate = 'relatedContracts',
  search,
  searchExact,
  sortBy = 'type',
  isBookmark,
  templateList,
  stripeInfo,
  onlyExternal,
  disableFetch,
  user,
  logo,
}) {
  const [list, setList] = useState([]);
  const history = useHistory();
  const [currentPage, setCurrentPage] = useState(1);
  const [isQuestionaireOpen, setIsQuestionaireOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(false);
  const modalsDispatch = useModalsDispatch();
  const { profile } = useProfile();
  // const searchExact = query.get('searchExact')
  const params = useMemo(
    () => ({
      perPage: 20,
      page: currentPage,
      jurisdiction,
      category,
      populate,
      type,
      search,
      searchExact,
      onlyTemplates: false,
      isPlaylist: false,
      sortBy,
      isBookmark,
      onlyExternal,
      user,
    }),
    [
      currentPage,
      jurisdiction,
      category,
      type,
      populate,
      search,
      searchExact,
      onlyExternal,
      sortBy,
      isBookmark,
      user,
    ]
  );
  const stripe = useStripe();
  const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } =
    useInfinitePublicTemplateList(params, {
      enabled: !templateList,
      onSuccess: (payload) => {
        setList(payload?.pages?.flatMap?.((p) => p.data) ?? []);
      },
      getNextPageParam: ({ numPages, page }) =>
        Number(page) < Number(numPages) ? Number(page) + 1 : false,
    });
  const { mutate: grabPublicTemplateMutate } = useGrabPublicTemplate();
  const { mutate: bookmarkPublicTemplateMutate } = useBookmarkPublicTemplate();
  const { mutate: unbookmarkPublicTemplateMutate } =
    useUnbookmarkPublicTemplate();
  const { mutate: viewPublicTemplateMutate } = useViewPublicTemplate();

  const handleGrab = (publicTemplate) => {
    grabPublicTemplateMutate({
      publicTemplateId: publicTemplate._id,
    });
  };
  const handleEdit = (publicTemplate) => {
    history.push(`/public-templates/${publicTemplate._id}/edit`);
  };
  const handlePreview = (publicTemplate) => {
    modalsDispatch({
      type: OPEN_PREVIEW,
      template: {
        ...publicTemplate.template,
        previewType: publicTemplate.type,
      },
      center: true,
    });
    viewPublicTemplateMutate({
      publicTemplateId: publicTemplate._id,
    });
  };

  const handleBookmark = (publicTemplate) => {
    if (publicTemplate.isBookmark) {
      unbookmarkPublicTemplateMutate({
        publicTemplateId: publicTemplate._id,
      });
    } else {
      bookmarkPublicTemplateMutate({
        publicTemplateId: publicTemplate._id,
      });
    }
  };
  const handlePurchase = async (publicTemplate) => {
    const { id } = publicTemplate.stripeInfo;
    await stripe.redirectToCheckout({
      successUrl: `${
        config.baseUrl
      }/checkout/success?productId=${id}&productName=${encodeURI(
        publicTemplate.name
      )}&goToMyTemplates=true`,
      cancelUrl: `${config.baseUrl}/templates/catalog?tab=catalog`,
      mode: 'payment',
      lineItems: [
        {
          price: id,
          quantity: 1,
        },
      ],
      clientReferenceId: profile.organization,
      customerEmail: profile.email,
      // locale: 'mx'
    });
  };
  const handleUse = async (publicTemplate = {}, disabled) => {
    // scrollToMyTemplate()
    const template = publicTemplate.userTemplate;
    if (!template) return;
    const templateId = template?._id;
    if (template.isTestament) {
      history.push(`/templates/${templateId}/testaments/create`);
    } else if (!template.questions?.length) {
      history.push(`/templates/${templateId}/documents/create`);
    } else {
      setSelectedTemplate(template);
      setIsQuestionaireOpen(true);
    }
  };

  const toggleQuestionaire = () => {
    setIsQuestionaireOpen((isOpen) => !isOpen);
  };

  return (
    <>
      <TemplateFolder
        name={label}
        noResultsText={noResultsText}
        count={templateList?.length || data?.pages?.[0]?.total}
        isLoading={isLoading}
        defaultIsOpen={defaultIsOpen}
        isFetchingNextPage={isFetchingNextPage}
        templateList={templateList || list}
        stripeInfo={stripeInfo}
        logo={logo}
        onGrab={handleGrab}
        onEdit={handleEdit}
        onPreview={handlePreview}
        onBookmark={handleBookmark}
        onPurchase={handlePurchase}
        onUse={handleUse}
      />
      <CreateContractFromQuestionaire
        isOpen={isQuestionaireOpen}
        questions={selectedTemplate.questions}
        templateId={selectedTemplate.templateId ?? selectedTemplate._id}
        toggle={toggleQuestionaire}
      />
    </>
  );
}

export function TemplateFolderContainer({ children, defaultIsOpen = true }) {
  const [isOpen, setIsOpen] = useState(defaultIsOpen);

  const toggle = () => setIsOpen(!isOpen);

  const childrenWithProps = cloneElementsWithProps(children, {
    isOpen,
    toggle,
  });

  return (
    <Container>
      <div className="m-2">{childrenWithProps}</div>
    </Container>
  );
}

function TemplateFolderHeader({
  isFetchingNextPage,
  isLoading,
  name,
  totalDocuments,
  isOpen,
  toggle,
  stripeInfo,
  type,
  onPurchase,
}) {
  const { t } = useTranslation();

  const handlePurchase = () => {
    onPurchase({ name, stripeInfo });
  };

  return (
    <div className="d-flex align-items-center">
      <div className="d-flex align-items-center position-relative">
        <FontAwesomeIcon
          className="text-accent"
          icon={isOpen ? 'chevron-down' : 'chevron-up'}
          onClick={toggle}
        />
        <FontAwesomeIcon
          className="text-accent ml-2"
          icon={['far', 'folder']}
          onClick={toggle}
        />
        <span
          className="font-weight-bold font-size-14 ml-2 text-nowrap"
          onClick={toggle}
        >
          {name}
        </span>
        <span className="ml-2" onClick={toggle} hidden={isLoading}>
          ({`${totalDocuments || 0}`})
        </span>
      </div>
      <div className="w-100 ml-2 d-flex align-items-center">
        <hr className="d-inline-block w-100" />
      </div>
      {isFetchingNextPage && 'loading'}
      {stripeInfo && (
        <div className="d-flex align-items-center">
          <div className="font-weight-bold text-nowrap ml-2">
            {getPrice(stripeInfo?.price, stripeInfo?.currency, true)}
          </div>
          <Button
            className="ml-3"
            color={'accent-100'}
            onClick={handlePurchase}
          >
            {t('buy')}
          </Button>
        </div>
      )}
    </div>
  );
}
function TemplateFolderBody({ isOpen, children }) {
  return (
    <Collapse isOpen={isOpen}>
      <Row className="p-3 flex-nowrap overflow-auto">{children}</Row>
    </Collapse>
  );
}

export function TemplateFolder({
  name,
  count,
  isRequestedTemplate,
  isLoading,
  isFetchingNextPage,
  noResultsText = 'noResults',
  templateList = [],
  stripeInfo,
  isOwned,
  defaultIsOpen,
  onUse,
  onPurchase,
  onPreview,
  onBookmark,
  onEdit,
  onGrab,
  onDelete,
}) {
  const { t } = useTranslation();

  return (
    <TemplateFolderContainer defaultIsOpen={defaultIsOpen}>
      <TemplateFolderHeader
        name={name}
        isLoading={isLoading}
        totalDocuments={count}
        isFetchingNextPage={isFetchingNextPage}
        stripeInfo={stripeInfo}
        onPurchase={onPurchase}
      />
      <TemplateFolderBody>
        {isLoading ? (
          <Spinner />
        ) : !templateList.length ? (
          t(noResultsText)
        ) : (
          templateList.map((template) => (
            <Col sm={3}>
              <TemplateItem
                isRequestedTemplate={isRequestedTemplate}
                template={template}
                isOwned={isOwned}
                onGrab={onGrab}
                onEdit={onEdit}
                onPreview={onPreview}
                onBookmark={onBookmark}
                onPurchase={onPurchase}
                onUse={onUse}
                logo={template?.user?.office?.logo}
                onDelete={onDelete}
              />
            </Col>
          ))
        )}
      </TemplateFolderBody>
    </TemplateFolderContainer>
  );
}

function getStatusBars(status) {
  let bars = ['#eaeaea', '#eaeaea', '#eaeaea'];

  if (['REQUESTED', 'IN_REVIEW'].includes(status)) {
    bars[0] = '#f37b33';
  }
  if (['IN_REVIEW'].includes(status)) {
    bars[1] = '#f37b33';
  }
  if (['APPROVED'].includes(status)) {
    bars = bars.map((b) => '#01cc40');
  }
  if (['REJECTED'].includes(status)) {
    bars = bars.map((b) => '#f53d3d');
  }

  return bars;
}

function getIcon(status) {
  let resp = {};

  if (['REQUESTED', 'IN_REVIEW'].includes(status)) {
    resp.icon = 'stopwatch';
  }
  if (['APPROVED'].includes(status)) {
    resp.icon = 'bell';
    resp.color = '#f37b33';
  }
  if (['REJECTED'].includes(status)) {
    resp.icon = 'times';
    resp.color = '#f53d3d';
  }

  return resp;
}

export function TemplateItem({
  template = {},
  isOwned,
  isRequestedTemplate,
  logo,
  onPurchase,
  onPreview,
  onUse,
  onBookmark,
  onEdit,
  onGrab,
  onDelete,
}) {
  const { t } = useTranslation();

  const handlePurchase = () => {
    onPurchase(template);
  };
  const handlePreview = () => {
    onPreview(template);
  };
  const handleBookmark = () => {
    onBookmark(template);
  };
  const handleEdit = () => {
    onEdit(template);
  };
  const handleGrab = () => {
    onGrab(template);
  };
  const handleUse = () => {
    onUse(template);
  };
  const handleDelete = () => {
    onDelete(template);
  };

  const statusBars = getStatusBars(template.status);
  const { icon, color } = getIcon(template.status);

  return (
    <Card style={{ minHeight: 300, position: 'relative' }}>
      <CardBody className="d-flex flex-column justify-content-between position-relative">
        <div>
          <div
            className={clsx(
              'font-weight-bold mb-3',
              isRequestedTemplate && 'd-flex justify-content-between '
            )}
          >
            <span>{template.name}</span>
            {isRequestedTemplate && (
              <FontAwesomeIcon icon={icon} color={color} />
            )}
          </div>

          <div
            className="text-muted mb-3 font-size-14"
            style={{ maxHeight: 100, overflowY: 'scroll' }}
          >
            {isRequestedTemplate && template.status === 'REJECTED'
              ? `Motivo: ${template.rejectReason || ''}`
              : template.description}
          </div>
        </div>
        <div className="font-weight-bold font-size-24" hidden={isOwned}>
          {template.type === 'premium'
            ? getPrice(
                template.stripeInfo?.price,
                template.stripeInfo?.currency
              )
            : t('free')}
        </div>
        {logo && (
          <img
            src={logo}
            classname="rounded"
            style={{
              position: 'absolute',
              bottom: 10,
              right: 10,
              width: 50,
              height: 50,
            }}
            alt="logo"
          />
        )}
        <div hidden={!isRequestedTemplate}>
          <div className="d-flex justify-content-between">
            <span>Estatus</span>
            <span>{t(`documentStatus.${template.status}`)}</span>
          </div>
          <div className="d-flex justify-content-around mt-2">
            {statusBars.map((bar) => (
              <div
                className="rounded"
                style={{ width: '32%', background: bar, height: 5 }}
              ></div>
            ))}
          </div>
        </div>
      </CardBody>
      <CardFooter className="bg-white d-flex justify-content-evenly p-3">
        <TemplateItemIcon
          icon="shopping-cart"
          id={template._id}
          onClick={handlePurchase}
          hidden={
            template.isPurchased || template.type !== 'premium' || !onPurchase
          }
          tooltip={t('buy')}
        />
        <TemplateItemIcon
          icon="hand-paper"
          id={template._id}
          onClick={handleGrab}
          hidden={template.isPurchased || template.type !== 'free' || !onGrab}
          tooltip={t('use')}
        />
        <TemplateItemIcon
          icon="plus-square"
          id={template._id}
          onClick={handleUse}
          hidden={
            isRequestedTemplate ||
            (!isOwned && (!template.isPurchased || !onUse))
          }
          tooltip={'create'}
        />
        <TemplateItemIcon
          icon="eye"
          id={template._id}
          onClick={handlePreview}
          hidden={!onPreview}
        />
        <TemplateItemIcon
          icon="edit"
          id={template._id}
          hidden={!onEdit}
          onClick={handleEdit}
        />
        <TemplateItemIcon
          icon={['far', 'bookmark']}
          id={template._id}
          color={template.isBookmark ? 'accent' : undefined}
          hidden={!onBookmark}
          onClick={handleBookmark}
        />
        <TemplateItemIcon
          icon={'trash'}
          id={template._id}
          hidden={!onDelete || !isRequestedTemplate}
          onClick={handleDelete}
        />
      </CardFooter>
    </Card>
  );
}

function TemplateItemIcon({
  className,
  color = 'muted',
  id,
  icon,
  onClick,
  hidden,
  tooltip,
}) {
  const targetId = `template-item-icon-${id}-${
    Array.isArray(icon) ? icon[icon.length - 1] : icon
  }`;

  if (hidden) {
    return null;
  }

  return (
    <>
      <FontAwesomeIcon
        id={targetId}
        className={clsx('cursor-pointer', className, `text-${color}`)}
        icon={icon}
        onClick={onClick}
      />
      {tooltip && (
        <UncontrolledTooltip target={targetId} placement="top">
          {tooltip}
        </UncontrolledTooltip>
      )}
    </>
  );
}

function getInitialValues(questions) {
  return {
    questions: questions.reduce((obj, question) => {
      if (question.answers.length) {
        obj[question._id] = question.answers[0]._id;
      }
      return obj;
    }, {}),
  };
}

function getSchema(questions) {
  return Yup.object().shape({
    questions: Yup.object().shape(
      questions.reduce((obj, question) => {
        obj[question._id] = Yup.string();
        return obj;
      }, {})
    ),
  });
}

export function CreateContractFromQuestionaire({
  isOpen,
  toggle,
  templateId,
  questions = [],
}) {
  const history = useHistory();
  const { t } = useTranslation('common');
  const initialValues = useMemo(() => {
    if (questions) {
      return getInitialValues(questions);
    }
    return {};
  }, [questions]);

  const Schema = useMemo(() => {
    if (questions) {
      return getSchema(questions);
    }
    return {};
  }, [questions]);
  const { mutate, isLoading: creating } = useCreateContract({
    onSuccess: (payload) => {
      history.push(`/documents/${payload.data._id}/edit`);
    },
  });
  const formik = useFormik({
    initialValues,
    onSubmit: async (values, actions) => {
      await mutate({
        ...values,
        templateId,
      });
      actions.setSubmitting(false);
    },
    enableReinitialize: true,
    validationSchema: Schema,
  });

  const handleQuestionChange = (questionId, answerId) => (evt) => {
    formik.setFieldValue(`questions.${questionId}`, answerId);
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle}>
      <ModalHeader toggle={toggle} className="questionaire-modal">
        <div className="d-flex flex-column">
          {t('actions.createDocument')}
          <small>{t('answerQuestions')}:</small>
        </div>
      </ModalHeader>
      <ModalBody className="mxh-40vh overflow-auto">
        <Questionare
          questions={questions}
          values={formik?.values?.questions ?? {}}
          onChange={handleQuestionChange}
          formik={formik}
          hideVariables
        />
      </ModalBody>
      <ModalFooter>
        <CustomButton
          className="ml-1"
          color="primary"
          size="md"
          // onClick={handleRequestTemplate}
          onClick={toggle}
          disabled={creating}
          // tooltip={t('noMoreTemplates')}
          // help
          // helpProps={{
          //     title: t('actions.requestTemplate'),
          //     text: t('help.requestTemplate')
          // }}
        >
          {/* {t('actions.requestTemplate')} */}
          {t('actions.cancel')}
        </CustomButton>
        <CustomButton
          className="ml-1"
          color="accent-100"
          size="md"
          onClick={formik.handleSubmit}
          loading={creating}
          loadingText={t('actions.creating')}
          // spinnerProps={{color: 'accent'}}
          disabled={creating}
          // tooltip={t('noMoreTemplates')}
          // help
          // helpProps={{
          //     title: t('actions.requestTemplate'),
          //     text: t('help.requestTemplate')
          // }}
        >
          {/* {t('actions.requestTemplate')} */}
          {t('actions.create')}
        </CustomButton>
      </ModalFooter>
    </Modal>
  );
}
